import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, Filter, X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import validationService from '../../services/validationService';
import storageService from '../../services/storageService';

const FileUploadValidation = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    entity: '',
    status: 'all', // all, fail, pass
    searchText: ''
  });

  useEffect(() => {
    loadUploadedFiles();
  }, []);

  const loadUploadedFiles = () => {
    const files = storageService.getUploadedFiles();
    setUploadedFiles(files);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      const results = [];
      let totalFileSize = 0;
      const allEntities = new Set();
      let totalRowsCount = 0;
      const allValidationResults = [];
      const fileNames = [];

      // Process all selected files
      for (const file of selectedFiles) {
        const result = await validationService.validateFile(file);
        results.push(result);
        
        totalFileSize += result.fileSize;
        result.entities.forEach(entity => allEntities.add(entity));
        totalRowsCount += result.totalRows;
        allValidationResults.push(...result.validationResults);
        fileNames.push(result.fileName);
      }

      // Merge results into one combined result
      const mergedResult = {
        fileName: selectedFiles.length === 1 ? fileNames[0] : `${selectedFiles.length} files: ${fileNames.join(', ')}`,
        fileSize: totalFileSize,
        entities: Array.from(allEntities),
        totalRows: totalRowsCount,
        validationResults: allValidationResults,
        hasErrors: allValidationResults.length > 0,
        validatedAt: new Date().toISOString(),
        fileCount: selectedFiles.length,
        fileNames: fileNames
      };

      // Save to localStorage
      const fileRecord = {
        id: `file${Date.now()}`,
        ...mergedResult
      };
      storageService.saveUploadedFile(fileRecord);
      
      setValidationResult(mergedResult);
      loadUploadedFiles();
      setSelectedFiles([]);
      
      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (error) {
      alert(`Lỗi khi xử lý file: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewResult = (file) => {
    setValidationResult(file);
  };

  const handleExportResult = () => {
    if (validationResult) {
      validationService.exportResults(validationResult);
    }
  };

  const handleClearResult = () => {
    setValidationResult(null);
  };

  const getFilteredResults = () => {
    if (!validationResult || !validationResult.validationResults) return [];

    let filtered = validationResult.validationResults;

    // Filter by entity
    if (filters.entity) {
      filtered = filtered.filter(r => r.entityCode === filters.entity);
    }

    // Filter by search text
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      filtered = filtered.map(r => ({
        ...r,
        validations: r.validations.filter(v => 
          v.column.toLowerCase().includes(search) ||
          v.message.toLowerCase().includes(search) ||
          String(v.value).toLowerCase().includes(search)
        )
      })).filter(r => r.validations.length > 0);
    }

    return filtered;
  };

  const getTotalErrors = () => {
    if (!validationResult || !validationResult.validationResults) return 0;
    return validationResult.validationResults.reduce(
      (sum, r) => sum + r.validations.length, 
      0
    );
  };

  const getUniqueEntities = () => {
    if (!validationResult || !validationResult.validationResults) return [];
    return [...new Set(validationResult.validationResults.map(r => r.entityCode))];
  };

  const filteredResults = getFilteredResults();
  const totalErrors = getTotalErrors();

  return (
    <div className="file-upload-validation">
      <div className="header">
        <h2><Upload size={24} /> Upload & Validation</h2>
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <div className="upload-card">
          <div className="upload-icon">
            <FileText size={48} />
          </div>
          <h3>Upload File để Kiểm tra</h3>
          <p>Hỗ trợ file Excel (.xlsx, .xls) và CSV</p>
          <p className="hint">Excel: Mỗi sheet là một đối tượng | CSV: Chọn nhiều file để validate nhiều đối tượng</p>
          
          <div className="upload-controls">
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input" className="btn btn-secondary">
              <FileText size={16} /> Chọn File
            </label>
            
            {selectedFiles.length > 0 && (
              <div className="selected-files-container">
                <div className="selected-files-header">
                  <strong>{selectedFiles.length} file được chọn</strong>
                  <button 
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Đang xử lý...' : 'Upload & Validate'}
                  </button>
                </div>
                <div className="selected-files-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="selected-file-item">
                      <FileText size={16} />
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                      <button 
                        className="btn-remove"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isUploading}
                        title="Xóa file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload History */}
      {uploadedFiles.length > 0 && !validationResult && (
        <div className="upload-history">
          <h3>Lịch sử Upload</h3>
          <div className="history-list">
            {uploadedFiles.map(file => (
              <div key={file.id} className="history-item">
                <div className="file-info">
                  <FileText size={20} />
                  <div>
                    <strong>{file.fileName}</strong>
                    <small>
                      {file.validatedAt 
                        ? new Date(file.validatedAt).toLocaleString('vi-VN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Chưa có thông tin'
                      }
                    </small>
                  </div>
                </div>
                <div className="file-stats">
                  <span className={file.hasErrors ? 'status-error' : 'status-success'}>
                    {file.hasErrors ? <XCircle size={16} /> : <CheckCircle size={16} />}
                    {file.hasErrors ? 'Có lỗi' : 'Hợp lệ'}
                  </span>
                  <span>{file.totalRows} dòng</span>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleViewResult(file)}
                  >
                    Xem chi tiết
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation Results */}
      {validationResult && (
        <div className="validation-results">
          <div className="results-header">
            <div className="results-title">
              <h3>Kết quả Validation</h3>
              <button 
                className="btn-icon"
                onClick={handleClearResult}
                title="Đóng"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="results-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleExportResult}
              >
                <Download size={16} /> Xuất Excel
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="results-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <FileText size={24} />
              </div>
              <div className="summary-info">
                <strong>
                  {validationResult.fileCount && validationResult.fileCount > 1 
                    ? `${validationResult.fileCount} files`
                    : validationResult.fileName
                  }
                </strong>
                <small>{(validationResult.fileSize / 1024).toFixed(2)} KB</small>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">
                {validationResult.hasErrors ? (
                  <XCircle size={24} className="icon-error" />
                ) : (
                  <CheckCircle size={24} className="icon-success" />
                )}
              </div>
              <div className="summary-info">
                <strong>{validationResult.hasErrors ? 'CÓ LỖI' : 'HỢP LỆ'}</strong>
                <small>{totalErrors} lỗi</small>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">
                <AlertCircle size={24} />
              </div>
              <div className="summary-info">
                <strong>{validationResult.totalRows} dòng</strong>
                <small>{validationResult.entities.length} đối tượng</small>
              </div>
            </div>
          </div>

          {/* File Names List (for multiple files) */}
          {validationResult.fileCount && validationResult.fileCount > 1 && validationResult.fileNames && (
            <div className="file-names-list">
              <h4>Files đã upload ({validationResult.fileCount}):</h4>
              <ul>
                {validationResult.fileNames.map((fileName, index) => (
                  <li key={index}>
                    <FileText size={14} />
                    <span>{fileName}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Filters */}
          {validationResult.hasErrors && (
            <div className="results-filters">
              <div className="filter-group">
                <Filter size={16} />
                <select
                  value={filters.entity}
                  onChange={(e) => setFilters({...filters, entity: e.target.value})}
                >
                  <option value="">Tất cả đối tượng</option>
                  {getUniqueEntities().map(entity => (
                    <option key={entity} value={entity}>{entity}</option>
                  ))}
                </select>
                
                <input
                  type="text"
                  placeholder="Tìm kiếm..."
                  value={filters.searchText}
                  onChange={(e) => setFilters({...filters, searchText: e.target.value})}
                />
                
                {(filters.entity || filters.searchText) && (
                  <button 
                    className="btn-clear-filter"
                    onClick={() => setFilters({ entity: '', status: 'all', searchText: '' })}
                  >
                    <X size={14} /> Xóa bộ lọc
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Errors Table */}
          {validationResult.hasErrors ? (
            <div className="results-table">
              {filteredResults.length === 0 ? (
                <div className="empty-result">
                  <p>Không tìm thấy kết quả phù hợp với bộ lọc</p>
                </div>
              ) : (
                filteredResults.map((result, idx) => (
                  <div key={idx} className="entity-results">
                    <div className="entity-header">
                      <h4>{result.entity}</h4>
                      <span className="badge badge-error">
                        {result.validations.length} lỗi
                      </span>
                    </div>
                    
                    <table className="table table-errors">
                      <thead>
                        <tr>
                          <th>Dòng</th>
                          <th>Cột</th>
                          <th>Giá trị</th>
                          <th>Quy tắc</th>
                          <th>Lỗi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.validations.map((validation, vIdx) => (
                          <tr key={vIdx}>
                            <td className="row-number">{validation.row}</td>
                            <td><code>{validation.column}</code></td>
                            <td className="cell-value">
                              {validation.value === null || validation.value === undefined || validation.value === '' ? (
                                <em className="empty-value">(trống)</em>
                              ) : (
                                String(validation.value)
                              )}
                            </td>
                            <td>{validation.rule}</td>
                            <td className="error-message">
                              <XCircle size={14} className="icon-error" />
                              {validation.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="success-message">
              <CheckCircle size={48} className="icon-success" />
              <h3>Dữ liệu hợp lệ!</h3>
              <p>Tất cả các bản ghi đều vượt qua kiểm tra validation</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadValidation;
